import { useCallback, useEffect, useMemo, memo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import type { Patient } from "fhir/r5";

import { LargeContentBox } from "@/components/common";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";
import {
  QuestionnaireRespondView,
  useQuestionnaireRespond,
  type QuestionnaireRespondSubmitSuccessMeta,
} from "src-next/modules/questionnaire-builder";
import { postQuestionnaireAnsweredNotificationTask } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import {
  clearPatientQuestionnaireLinkFlow,
  clearPatientQuestionnaireLinkNotifierContext,
  peekPatientQuestionnaireLinkNotifierContext,
} from "src-next/shared/patientQuestionnaireLink";

function getPatientDisplayName(patient: Patient | undefined): string {
  const fromText = patient?.name?.[0]?.text?.trim();
  return fromText || "Paciente";
}

interface PatientQuestionnaireRespondBodyProps {
  questionnaireId: string;
  subjectReference: string;
  onLeave: () => void;
  onSubmitSuccess: (meta: QuestionnaireRespondSubmitSuccessMeta) => void;
}

const PatientQuestionnaireRespondBody = memo(
  ({
    questionnaireId,
    subjectReference,
    onLeave,
    onSubmitSuccess,
  }: PatientQuestionnaireRespondBodyProps) => {
    const hookResult = useQuestionnaireRespond({
      questionnaireId,
      subjectReference,
      onSubmitSuccess,
    });

    return (
      <Box sx={{ width: "100%", maxWidth: 960, mx: "auto" }}>
        <QuestionnaireRespondView
          {...hookResult}
          variant="patientPortal"
          onLeavePlatform={onLeave}
        />
      </Box>
    );
  },
);

PatientQuestionnaireRespondBody.displayName = "PatientQuestionnaireRespondBody";

export function PatientQuestionnairePage() {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const navigate = useNavigate();
  const { userPatient, loading: sessionLoading } = useSessionContext();
  const [showThankYou, setShowThankYou] = useState(false);

  useEffect(() => {
    if (!questionnaireId) return;
    const notifier = peekPatientQuestionnaireLinkNotifierContext();
    if (notifier && notifier.questionnaireId !== questionnaireId) {
      clearPatientQuestionnaireLinkNotifierContext();
    }
  }, [questionnaireId]);

  const subjectReference = useMemo(() => {
    const patientId = userPatient?.id;
    if (!patientId) return "";
    const patientRef =
      /^\d+$/.test(String(patientId))
        ? `${oids.cpf}-${patientId}`
        : patientId;
    return `Patient/${patientRef}`;
  }, [userPatient?.id]);

  const handleGoToPlatform = useCallback(() => {
    clearPatientQuestionnaireLinkFlow();
    navigate("/", { replace: true });
  }, [navigate]);

  const handleSubmitSuccess = useCallback(
    (meta: QuestionnaireRespondSubmitSuccessMeta) => {
      void (async () => {
        const notifier = peekPatientQuestionnaireLinkNotifierContext();
        if (
          notifier &&
          questionnaireId &&
          notifier.questionnaireId === questionnaireId
        ) {
          try {
            await postQuestionnaireAnsweredNotificationTask({
              patientDisplayName: getPatientDisplayName(userPatient),
              questionnaireTitle: meta.questionnaireTitle,
              questionnaireId,
              subjectReference,
              requesterPractitionerRoleReference:
                notifier.practitionerRoleReference,
              ownerOrganizationReference: notifier.ownerOrganizationReference,
            });
          } catch (error) {
            console.error(
              "PatientQuestionnairePage create notification task",
              error,
            );
            toast.error(
              "Resposta enviada, mas não foi possível notificar a equipe via task.",
            );
          } finally {
            clearPatientQuestionnaireLinkNotifierContext();
          }
        }
        setShowThankYou(true);
      })();
    },
    [questionnaireId, subjectReference, userPatient],
  );

  if (!questionnaireId) {
    return (
      <LargeContentBox>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper variant="outlined" sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Alert severity="error">Questionário não informado.</Alert>
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={handleGoToPlatform}
                sx={{
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  p: 0,
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                Voltar à plataforma
              </Typography>
            </Stack>
          </Paper>
        </Container>
      </LargeContentBox>
    );
  }

  if (sessionLoading) {
    return (
      <LargeContentBox>
        <Stack
          width="100%"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Stack>
      </LargeContentBox>
    );
  }

  if (!subjectReference) {
    return (
      <LargeContentBox>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper variant="outlined" sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Alert severity="error">
                Não foi possível identificar seu cadastro de paciente. Tente sair e
                entrar novamente.
              </Alert>
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={handleGoToPlatform}
                sx={{
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  p: 0,
                  color: "primary.main",
                  textDecoration: "underline",
                }}
              >
                Voltar à plataforma
              </Typography>
            </Stack>
          </Paper>
        </Container>
      </LargeContentBox>
    );
  }

  if (showThankYou) {
    return (
      <LargeContentBox>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack spacing={3} alignItems="stretch">
              <Typography variant="h5" component="h1" fontWeight={700} textAlign="center">
                Obrigado pela sua resposta
              </Typography>
              <Typography color="text.secondary" textAlign="center" sx={{ lineHeight: 1.6 }}>
                Suas respostas foram enviadas com sucesso e já estão disponíveis para a equipe
                de saúde. Em breve alguém da clínica poderá utilizá-las no seu cuidado.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleGoToPlatform}
                sx={{ mt: 1 }}
              >
                Ir para a plataforma
              </Button>
            </Stack>
          </Paper>
        </Container>
      </LargeContentBox>
    );
  }

  return (
    <LargeContentBox>
      <PatientQuestionnaireRespondBody
        questionnaireId={questionnaireId}
        subjectReference={subjectReference}
        onLeave={handleGoToPlatform}
        onSubmitSuccess={handleSubmitSuccess}
      />
    </LargeContentBox>
  );
}
